// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { isSuiNSName } from '@mysten/core';
import { formatDigest } from '@mysten/sui.js/utils';
import { type ReactNode } from 'react';

import { Link, type LinkProps } from '~/ui/Link';
import { tryFormatCpcAddress } from '~/utils/cpcAddress';
import { shortenAddress } from '~/utils/explorerFormat';

interface BaseInternalLinkProps extends LinkProps {
	noTruncate?: boolean;
	label?: string | ReactNode;
	title?: string;
	queryStrings?: Record<string, string>;
}

function createInternalLink<T extends string>(
	base: string,
	propName: T,
	formatter: (id: string) => string = (id) => id,
) {
	return ({
		[propName]: id,
		noTruncate,
		label,
		title,
		queryStrings = {},
		...props
	}: BaseInternalLinkProps & Record<T, string>) => {
		const formatted = formatter(id);
		const display = noTruncate ? tryFormatCpcAddress(id) : formatted;
		const queryString = new URLSearchParams(queryStrings).toString();
		const queryStringPrefix = queryString ? `?${queryString}` : '';

		return (
			<Link
				variant="mono"
				to={`/${base}/${encodeURI(id)}${queryStringPrefix}`}
				title={title ?? tryFormatCpcAddress(id)}
				{...props}
			>
				{label || display}
			</Link>
		);
	};
}

export const EpochLink = createInternalLink('epoch', 'epoch');
export const CheckpointLink = createInternalLink('checkpoint', 'digest', shortenAddress);
export const CheckpointSequenceLink = createInternalLink('checkpoint', 'sequence');
export const AddressLink = createInternalLink('address', 'address', (addressOrNs) => {
	if (isSuiNSName(addressOrNs)) {
		return addressOrNs;
	}
	return shortenAddress(addressOrNs);
});
export const ObjectLink = createInternalLink('object', 'objectId', shortenAddress);
export const TransactionLink = createInternalLink('txblock', 'digest', formatDigest);
export const ValidatorLink = createInternalLink('validator', 'address', shortenAddress);
